import { useMemo } from "react"
import { Users, Eye } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { useLocale } from "../components/LocaleProvider"
import { useSiteVisits } from "../lib/queries"
import type { SiteVisitStat } from "../types"

function localDayKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function SiteVisitors() {
  const { t } = useLocale()
  const { data, isLoading } = useSiteVisits(30)
  const allRows: SiteVisitStat[] = data ?? []

  const stats = useMemo(() => {
    const today = localDayKey(new Date())
    const yesterday = localDayKey(new Date(Date.now() - 86400000))
    const rows = allRows
    const todayRow = rows.find((r) => r.date === today)
    const yesterdayRow = rows.find((r) => r.date === yesterday)

    let last7 = 0
    let last30 = 0
    let visits7 = 0
    let visits30 = 0
    rows.forEach((r) => {
      if (r.uniqueVisitors <= 0) return
      const ageDays = Math.round((Date.now() - new Date(`${r.date}T00:00:00`).getTime()) / 86400000)
      if (ageDays < 7) {
        last7 += r.uniqueVisitors
        visits7 += r.visits
      }
      if (ageDays < 30) {
        last30 += r.uniqueVisitors
        visits30 += r.visits
      }
    })

    return {
      todayUnique: todayRow?.uniqueVisitors ?? 0,
      todayVisits: todayRow?.visits ?? 0,
      yesterdayUnique: yesterdayRow?.uniqueVisitors ?? 0,
      yesterdayVisits: yesterdayRow?.visits ?? 0,
      last7,
      visits7,
      last30,
      visits30,
      max: Math.max(1, ...rows.map((r) => r.uniqueVisitors)),
    }
  }, [allRows])

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center text-muted-foreground">
        {t("loading")}
      </div>
    )
  }

  const cards = [
    { label: t("visitors_today"), unique: stats.todayUnique, visits: stats.todayVisits },
    {
      label: t("visitors_yesterday"),
      unique: stats.yesterdayUnique,
      visits: stats.yesterdayVisits,
    },
    { label: t("visitors_last_7_days"), unique: stats.last7, visits: stats.visits7 },
    { label: t("visitors_last_30_days"), unique: stats.last30, visits: stats.visits30 },
  ]

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground">{c.label}</p>
            <p className="mt-2 text-2xl font-semibold">{c.unique}</p>
            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {c.unique} {t("visitors_unique")}
              </span>
              <span className="inline-flex items-center gap-1">
                <Eye className="h-3.5 w-3.5" />
                {c.visits} {t("visitors_visits")}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-card">
        <div className="border-b px-4 py-3 text-sm font-medium">{t("visitors_daily")}</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">{t("th_date")}</TableHead>
              <TableHead>{t("visitors_unique")}</TableHead>
              <TableHead className="w-32">{t("visitors_visits")}</TableHead>
              <TableHead className="w-56">{t("th_chart")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stats.max <= 0 || allRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  {t("visitors_empty")}
                </TableCell>
              </TableRow>
            ) : (
              allRows.map((row) => (
                <TableRow key={row.date}>
                  <TableCell className="font-mono text-sm whitespace-nowrap">{row.date}</TableCell>
                  <TableCell className="text-sm font-medium">{row.uniqueVisitors}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{row.visits}</TableCell>
                  <TableCell>
                    <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${(row.uniqueVisitors / stats.max) * 100}%` }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default SiteVisitors
